import { useState, useMemo } from 'react';
import type { SkillAverage } from '../../../types/skills';
import { groupSkillsBySection, getHeatmapColor } from '../../../utils/groupSkillsUtils';

interface SkillSectionHeatmapProps {
  skillAverages: SkillAverage[];
  focusAreas: SkillAverage[];
}

/**
 * Skill section heatmap showing per-skill target attainment color-coded by section.
 * Focus areas (up to 3 lowest at_target_percent) are highlighted with a ring.
 * Tapping a skill tile toggles its description.
 *
 * Requirements: 5.1, 5.2, 5.3, 5.4, 5.5, 5.6, 5.7, 11.4
 */
export default function SkillSectionHeatmap({ skillAverages, focusAreas }: SkillSectionHeatmapProps) {
  const [expandedSkill, setExpandedSkill] = useState<string | null>(null);
  // All hooks MUST run unconditionally on every render (Rules of Hooks).
  // These useMemo calls were previously below the early return, so switching
  // between a group with assessment data and one without changed the hook count
  // and threw React error #310 ("Rendered more hooks than during the previous render").
  const focusIds = useMemo(() => new Set(focusAreas.map((f) => f.skill_id)), [focusAreas]);
  const sections = useMemo(() => groupSkillsBySection(skillAverages), [skillAverages]);

  const hasAssessments = skillAverages.some((s) => s.assessed_rider_count > 0);
  if (!hasAssessments) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-center text-sm text-gray-500">
        Insufficient assessment data
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-gray-700">Skill Heatmap</h3>
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-green-100 border border-green-300" />
            ≥ 75%
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-yellow-100 border border-yellow-300" />
            50–74%
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-red-100 border border-red-300" />
            &lt; 50%
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-3 w-3 rounded bg-gray-100 ring-2 ring-indigo-500" />
            Focus
          </span>
        </div>
      </div>
      {Array.from(sections.entries()).map(([section, skills]) => (
        <div key={section}>
          <h4 className="mb-2 text-sm font-medium text-gray-500 uppercase tracking-wide">
            {section}
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {skills.map((skill) => {
              const isFocus = focusIds.has(skill.skill_id);
              const colorClass = getHeatmapColor(skill.at_target_percent);
              const isExpanded = expandedSkill === skill.skill_id;
              return (
                <button
                  type="button"
                  key={skill.skill_id}
                  onClick={() => setExpandedSkill(isExpanded ? null : skill.skill_id)}
                  className={`rounded-lg px-3 py-2 text-left text-base font-medium transition-all ${colorClass}${isFocus ? ' ring-2 ring-offset-1 ring-indigo-500' : ''}`}
                >
                  <div>{skill.skill_name} — {Math.round(skill.at_target_percent)}%</div>
                  {isExpanded && skill.description && (
                    <p className="mt-1 text-sm font-normal opacity-80">{skill.description}</p>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
